/**
 * The proposal's own header — `docs/proposal.md` §6 item 1.
 *
 * **It is the company's header, not Rowted's.** The portal frame used to draw
 * `RowtedLogo` above every page; on this one page that is our brand on a
 * document somebody is about to sign with their landscaper, so the lockup here
 * is the company's logo — or, when none was uploaded, a tile with the first
 * letter of its name in the brand fill (§5.1). Never our mark as a fallback.
 *
 * 72px on a desktop with the tagline and the phone number, 56px on a phone
 * with a call button, which is the one control somebody standing in their
 * garden actually wants.
 *
 * **Download PDF is absent, not disabled** (§6 item 1, §15): the PDF is PR 9,
 * and a button that answers nothing teaches somebody the page is broken.
 */
import type { ProposalIdentity } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { IconCall } from '../../frame/icons.js'
import { brandLetter } from './brand.js'

export function ProposalTopBar({ identity, phone, narrow = false }: {
  identity: ProposalIdentity
  /** True on a phone: the bar loses its tagline and its written-out number. */
  phone: boolean
  /**
   * Below the summary rail (`RAIL_FROM`): the bar's contents sit over the
   * page's 740 column. A prop rather than `max-lg:`, because the builder's
   * iPad frame is 820px wide inside a window that is not, and a media query
   * there measures the window.
   */
  narrow?: boolean
}) {
  return (
    <header
      className={cn('relative z-[3] flex shrink-0 items-center justify-between',
        'border-b border-line bg-surface',
        phone ? 'h-14 px-4'
          /*
           * Board 9's 60px at a portrait iPad, and over the 740 column once
           * the window is wider than that column needs — a landscape iPad's
           * logo at 60px above a column starting at 220 reads as two pages.
           */
          : narrow ? 'h-[72px] px-[max(60px,calc((100%_-_740px)_/_2))]'
            : 'h-[72px] px-[60px]')}
    >
      <span className="flex min-w-0 items-center gap-[11px]">
        <Lockup identity={identity} size={phone ? 30 : 40} />
        <span className="flex min-w-0 flex-col gap-0.5">
          <b className={cn('truncate font-bold leading-[1.05] tracking-[-0.015em] text-text',
            phone ? 'text-[15px]' : 'text-[18px]')}>
            {identity.name}
          </b>
          {!phone && identity.tagline && (
            <span className="truncate text-[13px] font-medium leading-[1.1]
                             tracking-[0.02em] text-text-muted">
              {identity.tagline}
            </span>
          )}
        </span>
      </span>

      {identity.phone && (phone
        ? (
          <a
            href={`tel:${identity.phone}`}
            aria-label={`Call ${identity.name}`}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-field
                       border border-line text-text-body"
          >
            <IconCall size={18} />
          </a>
        )
        : (
          <a
            href={`tel:${identity.phone}`}
            className="flex shrink-0 items-center gap-2 text-[14px] text-text-body
                       no-underline hover:text-text"
          >
            <IconCall size={16} className="text-brand-tx" />
            <b className="font-semibold">{identity.phone}</b>
          </a>
        ))}
    </header>
  )
}

/**
 * The logo at its own aspect ratio, or a letter tile.
 *
 * `object-contain` with a height and a ceiling on the width, never a crop and
 * never a tile behind it (§5.1): a logo drawn into a square box is a logo
 * squashed, and squashing somebody's logo on their own proposal is the one
 * visual mistake they will notice first.
 */
export function Lockup({ identity, size }: { identity: ProposalIdentity; size: number }) {
  if (identity.logoUrl) {
    return (
      <img
        src={identity.logoUrl}
        alt={identity.name}
        style={{ height: size, maxWidth: 220 }}
        className="shrink-0 object-contain"
      />
    )
  }
  return (
    <span
      style={{ height: size, width: size, borderRadius: Math.round(size * 0.29) }}
      className="flex shrink-0 items-center justify-center bg-brand font-bold text-brand-on"
    >
      <span style={{ fontSize: Math.round(size * 0.46) }}>{brandLetter(identity.name)}</span>
    </span>
  )
}
